import { RightShipInspectionHelper } from './../../../../../../helpers/rightship-inspection-helper';
import { autoinject, transient, computedFrom, BindingEngine, observable } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import * as Enumerable from "linq";
import { RightShipInspectionClientModel, RightShipInspectionModel } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { ValidationController, ValidationControllerFactory, ValidationRules, validateTrigger } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { Guid } from "../../../../../../utilities/guid";
import { RightShipInspectionsService } from "../../../../../../services/rightship-inspections/rightship-inspections.service";
import { UnitOfWorkItem, UnitOfWorkObserver } from "../../../../../../utilities/unit-of-work-observer";
import { BaseValidationResponse } from "../../../../../../models/validations/validations.models";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import * as moment from "moment";
import { EmailValidator } from '../../../../../../utilities/email-validator';
import { EmailRecipientTypeEnum, SubtaskCorrespondenceEmailModel } from '../../../../../../models/vetting/vetting.models';
import { ValidationsClientService } from '../../../../../../services/validations/validations-client.service';

@autoinject()
@transient()
export class ChangeRightShipInspectionValidityDateDialog {

    private _unitOfWorkObserver: UnitOfWorkObserver<RightShipInspectionModel>;

    public newInspection: RightShipInspectionModel;
    public validator: ValidationController;
    public existingInspection: RightShipInspectionClientModel;
    public correspondenceMessage: string = "";
    public comments: string;
    public onSave: () => void;
    public isInspectionValidityTypeInMonths : boolean = true;
    public months: { id: number, name: string }[] = Enumerable.range(0, 13).select(x => {
        return {
            id: x,
            name: x == 1 ? "1 month" : `${x} months`
        }
    }).toArray();
    public emails: {
        email: string
    }[] = [];
    today = (() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);

        return date;
    })();

    @observable()
    public validUntilMonthsOffset: number;
    @observable()
    public validUntilDate: Date;

    public dateOfInspection;

    public requiresNewInspection: boolean = false;

    public notifyShipManager: boolean = true;
    public shipManagerEmails: SubtaskCorrespondenceEmailModel[] = [];
    private _correspondenceToShipManager: string = "";

    @computedFrom('validUntil', 'reasonTemplate', 'isInspectionValidityTypeInMonths', 'validUntilMonthsOffset', 'isInspectionValidityTypeInMonths', 'validUntilDate')
    get correspondenceToShipManager() {
        if(!this.reasonTemplate) return "";
        
        const _validUntilMonthsOffset = this.inspectionHelper.diffMonthsDays(this.dateOfInspection, this.validUntil).months;
        if(this.reasonTemplate.indexOf("{{newValidityDate}}") >= 0 && !(this.isInspectionValidityTypeInMonths && this.validUntilMonthsOffset || !this.isInspectionValidityTypeInMonths && this.validUntilDate)) return "";

        this._correspondenceToShipManager = this.reasonTemplate.replaceAll("{{newValidityDate}}", this.validUntil ? moment(this.validUntil).format("DD MMM YYYY"): "")
                                                .replaceAll("{{actualDateOfInspection}}", this.existingInspection.actualDateOfInspection ? moment(this.existingInspection.actualDateOfInspection).format("DD MMM YYYY"): "")
                                                .replaceAll("{{pscPlace}}", this.existingInspection.pscPlace)
                                                .replaceAll("{{pscDate}}", this.existingInspection.pscDate ? moment(this.existingInspection.pscDate).format("DD MMM YYYY"): "")
                                                .replaceAll("{{incidentDate}}", this.existingInspection.incidentDate ? moment(this.existingInspection.incidentDate).format("DD MMM YYYY"): "")
                                                .replaceAll("{{months}}", `${_validUntilMonthsOffset != 1 ? _validUntilMonthsOffset + " months" : _validUntilMonthsOffset + " month"}`);
        return this._correspondenceToShipManager;
    }

     set correspondenceToShipManager(val: string) {
         this._correspondenceToShipManager = val;
    }

    public changeValidityDateReasonId?: number;

    @computedFrom('validUntilMonthsOffset', 'validUntilDate', 'isInspectionValidityTypeInMonths')
    get validUntil() {
        let requestedDateOfInspection = CloneHelper.clone(this.validUntilMonthsOffset == 0 ? this.today : (this.existingInspection.actualDateOfInspection ? this.existingInspection.actualDateOfInspection : this.existingInspection.requestedDateOfInspection));
        
        if(typeof(requestedDateOfInspection) == "string")  requestedDateOfInspection = new Date(requestedDateOfInspection);
        
        if(this.isInspectionValidityTypeInMonths) {
            const date = moment(requestedDateOfInspection).add(this.validUntilMonthsOffset, 'months');
            return date.toDate();
        }
        return this.validUntilDate;
    }

    constructor(private dialogClientService: DialogClientService,
        private rightShipInspectionService: RightShipInspectionsService,
        private bindingEngine: BindingEngine,
        private notificationService: NotificationClientService,
        validationControllerFactory: ValidationControllerFactory, private inspectionHelper : RightShipInspectionHelper,
        protected validationsClientService: ValidationsClientService) {
        this.validator = validationControllerFactory.createForCurrentScope();
        this.validator.addRenderer(new CustomValidationRenderer());
        this.validator.changeTrigger(validateTrigger.manual);
    }

    activate(model: { item: RightShipInspectionClientModel, onSave: () => void }) {
        this.existingInspection = model.item;
        this.onSave = model.onSave;
        this.correspondenceMessage = `This message is sent as subject vessel has been marked for a RightShip inspection. An inspection request is entirely a matter for Owners where the following should be considered:
        <br>- Under current RightShip rules vessels over 8,000 DWT and 14 years of age, and all vessels over 25 years of age, must have a valid RightShip inspection for the vessel to be recommended to RightShip customers.
        <br>- Some RightShip customers have inspection requirements that exceed the standard RightShip rules (e.g irrespective of vessel’s age).
        <br>- Having a valid RightShip inspection badge, whether required by the above or not, may boost confidence in a vessel’s performance and upholding the industry safety standards. This will be lost at the time of inspection expiry.
        <br>An Inspection application can be submitted from the RightShip website by clicking on the button below.
        <br>`;

        this.dateOfInspection = model.item.actualDateOfInspection ?? model.item.requestedDateOfInspection ?? new Date();
        this.dateOfInspection = (typeof this.dateOfInspection == 'string')? new Date(this.dateOfInspection) : this.dateOfInspection;
        this.initializeItemForCreate();

        this._unitOfWorkObserver = UnitOfWorkObserver.getTrackingInstance(this.bindingEngine, this.newInspection, []);

        setTimeout(() => {

            this.initializeItemOverride();
            this.setRules();
            this.addEmail();
        });

        this.shipManagerEmails = CloneHelper.clone(this.existingInspection.emails).filter(x=> x.isShipManagerEmail || x.isTechnicalManagerEmail);
        if (this.existingInspection.correspondenceEmail) {
            const correspondenceEmail: SubtaskCorrespondenceEmailModel ={
                email: this.existingInspection.correspondenceEmail,
                emailRecipientTypeId: EmailRecipientTypeEnum.Cc,
                recipientType: '(Correspondence)'
            };
            this.shipManagerEmails.push(correspondenceEmail);
        }

        this.shipManagerEmails.forEach(x => {
            const rules = this.getShipManagerEmailRules(x);
            this.validator.addObject(x, rules);
        });
    }

    @computedFrom("validUntilMonthsOffset", "validUntilDate", "isInspectionValidityTypeInMonths")
    get isDateDefaulted(){
        const isDefaulted = this.isInspectionValidityTypeInMonths ? !this.validUntilMonthsOffset : !this.validUntilDate;

        if (!isDefaulted) {
            this.requiresNewInspection = false;
        }

        return isDefaulted;
    }

    close() {
        this.dialogClientService.hide();
    }

    setRules() {

        ValidationRules
            .ensure<ChangeRightShipInspectionValidityDateDialog, any>(x => x.emails)
            .required()
            .withMessage("At least 1 email required")
            .minItems(1)
            .withMessage("At least 1 email required")
            .when(() => this.newInspection.notifyShipManager)
            
            .ensure(x => x.shipManagerEmails)
            .required()
            .withMessage("At least 1 email required")
            .minItems(1)
            .withMessage("At least 1 email required")
            .when(() => this.notifyShipManager)
            
            .ensure(x => x.changeValidityDateReasonId)
            .required()
            .withMessage("Reason is required")

            .ensure(x => x.validUntilMonthsOffset)
            .required()
            .withMessage("Number of months is required")
            .when(x => x.isInspectionValidityTypeInMonths)

            .ensure(x => x.validUntilDate)
            .required()
            .withMessage("Date is required")            
            .when(x => !x.isInspectionValidityTypeInMonths)
            .on(this);

        this.validator.addObject(this);
    }

    removeEmail(index) {
        const deleted = this.emails.splice(index, 1);
        this.validator.removeObject(deleted[0]);
    }

    getEmailRules(email: { email: string }) {

        const rules = ValidationRules.ensure((x: { email: string }) => x.email)
            .satisfies(x => {
                return !((x == null || x.trim() == '') && this.newInspection.notifyShipManager) || !this.requiresNewInspection;
            })
            .withMessage("Email Required")
            .satisfies(x => EmailValidator.isValidEmail(x) || !this.requiresNewInspection)
            .on(email).rules;

        return rules;
    }

    addEmail() {
        const email = {
            email: ''
        };
        this.emails.push(email);

        const rules = this.getEmailRules(email);
        this.validator.addObject(email, rules);
    }

    public newInspectionNotifyShipManagerChanged() {
        if (!this.emails || this.emails.length <= 0) return;
        if (this.newInspection.notifyShipManager == false) {
            this.emails.forEach(x => {
                this.validator.removeObject(x);
            });
        }
        else {
            this.emails.forEach(x => {
                const rules = this.getEmailRules(x);
                this.validator.addObject(x, rules);
            });
        }
    }
    
    @computedFrom('dateOfInspection')
    get maxDate() {
        return moment(this.dateOfInspection).add(12, 'months');
    }

    protected initializeItemForCreate(): void {
        const item = <RightShipInspectionModel>{
            additionalEmailsToNotify: null,
            agentEmail: null,
            agentName: null,
            agentOfficeNumber: null,
            agentOfficeNumberWithoutCode: null,
            assignedToUserName: null,
            assignmentComment: null,
            hasVet: false,
            inspectionRequestComments: null,
            previouslyAssignedToUserName: null,
            rightShipInspectionId: 0,
            technicalManagerContactNumber: null,
            technicalManagerContactNumberWithoutCode: null,
            technicalManagerEmail: null,
            technicalManagerName: null,
            technicalManagmentCompany: null,
            correspondenceEmail: null,
            vesselId: 0,
            incidentId: null,
            pscInspectionId: null,
            vesselOwnerName: null,
            rightShipInspectionDeficiencies: [],
            invoices: [],
            agreeToSafetyScoreClause: false,
            agreeToTermsAndConditions: false,
            allDeficienciesReviewed: false,
            assignedDate: null,
            notifyShipManager: null,
            assignedTo: null,
            businessUnitId: null,
            cargoTypeId: null,
            countryId: null,
            currentRightShipInspectionStatusHistoryId: null,
            entityType: EntityTypes.RightShipInspection,
            estimatedArrivalDate: null,
            fullInspectionReportReviewed: null,
            identifier: Guid.generateGuid(),
            inspectingCompanyId: null,
            inspectorId: null,
            isVesselOwnerInformedAndAgreed: null,
            antiBriberyAndCorruptionPolicy: null,
            lengthOfTimeAtBerth: null,
            noLetterOfIndemnityWillBeSigned: false,
            bothOwnerAndManagerPromptlyReport: false,
            numberOfDeficiencies: 0,
            overallAssessmentId: null,
            rightShipInspectionTypeId: null,
            portId: null,
            previouslyAssignedTo: null,
            reasonForInspectionId: null,
            requestedDateOfInspection: null,
            actualDateOfInspection: null,
            inspectorContactNumber: null,
            inspectorEmail: null,
            berth: null,
            validityExpiryDate: null,
            requiresRightshipInspectionComments: null,
            rightShipInspectionVesselTypeId: null,
            isSelfUnloadingVessel: null,
            isUsingLngFuel: null,
            isEquippedWithGantryCranes: null,
            hasIceClassNotation: null,
            isGearlessVessel: null,
            useMobileInspectionWorkflow: null,
            isRoRoShip: null,
            isTimberCarrier: null,
            isCarryingGrainCargoAndHasBeenFumigatedInThePast12Months: null,
            isHybrid: false,
            captainName: null,
            captainEmail: null,
            isVesselInvolvedInSts: null
        };

        this.newInspection = item;
    }

    protected initializeItemOverride() {
        this.newInspection.rightShipInspectionTypeId = this.existingInspection.inspectionTypeId;
        this.newInspection.vesselId = this.existingInspection.vesselId;
        // this.newInspection.pscInspectionId = this.pscInspectionId;
        // this.newInspection.incidentId = this.incidentId;

        if (this.newInspection.invoices.length == 0)
            this.newInspection.invoices.push({
                inspectionMarkup: null,
                invoiceReceivedDate: null,
                address: null,
                billingCustomerId: null,
                city: null,
                companyName: null,
                contactName: null,
                countryId: null,
                email: null,
                additionalEmailsToNotify: null,
                invoiceNumber: null,
                invoiceStatusId: null,
                phoneNumber: null,
                postCode: null,
                billingInstructions: null,
                supplierInspectionAmount: 0,
                supplierTravelAndExpenseAmount: 0,
                state: null
            });
    }

    protected saveItem(id: number, changes: { [index: string]: UnitOfWorkItem; }): Promise<BaseValidationResponse> {
        return this.rightShipInspectionService.saveRightShipInspection({
            id: id,
            changes: changes,
            notifyShipManager: this.newInspection.notifyShipManager,
            correspondenceMessage: this.correspondenceMessage
        });
    }

    reasonsForRSInspectionLoaded($event) {
        if (this.newInspection.reasonForInspectionId > 0) return;
        if ($event && $event.detail && $event.detail.values && Array.isArray($event.detail.values) && $event.detail.values.length > 0) {
            const values = $event.detail.values;

            if (values.filter(x => x.isDefault == true).length > 0 && !this.newInspection.reasonForInspectionId) {
                this.newInspection.reasonForInspectionId = values.filter(x => x.isDefault == true)[0].id;
            }
            else if (!this.newInspection.reasonForInspectionId) {
                this.newInspection.reasonForInspectionId = values[0].id;
            }
        }
    }

    @computedFrom("isInspectionValidityTypeInMonths", "validUntilDate", "validUntilMonthsOffset")
    get validUntilText() {
        return this.inspectionHelper.getValidUntilText(this.dateOfInspection, this.validUntil)
    }

    public async beforeSave(): Promise<boolean> {
        const validateResult = await this.validator.validate();
        if (validateResult.valid && this.requiresNewInspection) {
            if (this.emails?.filter(x => x.email != null && x.email.trim() != '').length > 0) {
                this.newInspection.correspondenceEmail = this.emails[0].email;
                const validEmails = this.emails.filter(x => x.email != null && x.email.trim() != '')
                    .slice(1) //filter out first email which would be set in the correspondence email
                    .map(x => x.email);
                if (validEmails.length > 0) {
                    this.newInspection.additionalEmailsToNotify = validEmails.join(';');
                }
            }
            return true;
        }
        return validateResult.valid;
    }

    public save(): Promise<void> {
        const saveAsync: () => Promise<void> = async () => {
            const validateResult = await this.beforeSave();
            if (!validateResult) {
                throw new Error("Validation failed");
            }
            try {
                await this.rightShipInspectionService.changeRightShipInspectionValidityDate({
                    validityExpiryDate: this.validUntil,
                    rightShipInspectionId: this.existingInspection.rightShipInspectionId,
                    comments: this.comments,
                    requiresNewInspection: this.requiresNewInspection,
                    notifyShipManager: this.notifyShipManager,
                    shipManagerEmails: this.shipManagerEmails.filter(x => x.email),
                    correspondenceToShipManager: this._correspondenceToShipManager,
                    changeValidityDateReasonId: this.changeValidityDateReasonId
                });
                if (this.requiresNewInspection) {
                    const response = await this.rightShipInspectionService.getRequiredRightShipInspection(this.existingInspection.vesselId, this.existingInspection.inspectionTypeId, false);
                    await this.rightShipInspectionService.saveRightShipInspection({
                        id: response.rightShipInspection != null ? response.rightShipInspection.rightShipInspectionId : 0,
                        changes: this._unitOfWorkObserver.getChanges(),
                        notifyShipManager: this.newInspection.notifyShipManager,
                        correspondenceMessage: this.correspondenceMessage
                    });
                }
                this.close();
                this.onSave();
                this.notificationService.show("Changed Validity Date successfully", "", NotificationType.Success);
            } catch (e) {
                if (e && e.content && e.content.validationResult && e.content.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(e.content.validationResult);
                } else {
                    this.notificationService.show("Failed to change validity date", "", NotificationType.Error);
                }
                throw e;
            }
        }
        return new Promise<void>((resolve, reject) => {           
            saveAsync().then(() => {
                resolve();
            }).catch((e) => {
                reject(e);
            });
        });
    }

    getShipManagerEmailRules(email: { email: string }) {

        const rules = ValidationRules.ensure((x: { email: string }) => x.email)
            .satisfies(x => {
                return !(x == null || x.trim() == '') || !this.notifyShipManager;
            })
            .withMessage("Email Required")
            .satisfies(x => EmailValidator.isValidEmail(x) || !this.notifyShipManager)
            .on(email).rules;

        return rules;
    }

    removeShipManagerEmail(index) {
        const deleted = this.shipManagerEmails.splice(index, 1);
        this.validator.removeObject(deleted[0]);
    }

    addShipManagerEmail(tech: boolean, ship: boolean, comp: boolean, defaultToCCType: boolean = false) {
        const email = {
            email: '',
            emailRecipientTypeId: defaultToCCType ? EmailRecipientTypeEnum.Cc : null,
            isInspectingCompanyEmail: comp,
            isShipManagerEmail: ship,
            isFromConfig: false,
            isTechnicalManagerEmail: tech
        };
        this.shipManagerEmails.push(email);
        const rules = this.getShipManagerEmailRules(email);
        this.validator.addObject(email, rules);
        return email;
    }

    public notifyShipManagerChanged() {
        if (!this.shipManagerEmails || this.shipManagerEmails.length <= 0) return;
        if (this.notifyShipManager == false) {
            this.shipManagerEmails.forEach(x => {
                this.validator.removeObject(x);
            });
        }
        else {
            this.shipManagerEmails.forEach(x => {
                const rules = this.getShipManagerEmailRules(x);
                this.validator.addObject(x, rules);
            });
        }
    }

    reasonTemplate: string;
    validityDateReasonChanged($event)
    {
        this.reasonTemplate = $event.detail.item?.additionalData?.template;        
    }
}